// The social side of the back room: reactions (emotes on your TV face), text
// chat limits, cigars, and the slot machine's math. Shared by the client and
// the server so both agree on the rules.
import { z } from "zod";
import { ITEMS_BY_ID } from "./items";
import { secureRng, type Rng } from "./rng";

// ---------- reactions ----------

/** Emotes a player can show on their TV face for a moment. */
export const EMOTES = ["XD", ":3", ":P", ">:(", ":)", ": |"] as const;
export const EmoteSchema = z.enum(EMOTES);
export type Emote = z.infer<typeof EmoteSchema>;
/**
 * A face on the TV: one of the free emotes, or the item id of a face from the
 * shop ("Reactions / Faces", ids 9xxxxxxx) that the player owns.
 */
export const FaceSchema = z.union([EmoteSchema, z.string().regex(/^9\d{7}$/)]);
export type Face = z.infer<typeof FaceSchema>;

/** The text a face shows on the TV screen. */
export function faceText(face: Face): string {
  if ((EMOTES as readonly string[]).includes(face)) return face;
  return ITEMS_BY_ID.get(face)?.face ?? ":)";
}

/** How long a reaction counts as recent (the TV face itself stays until the next reaction). */
export const REACTION_MS = 3000;
/** At most one reaction per this long per player (server-enforced). */
export const REACTION_COOLDOWN_MS = 1000;

// ---------- chat ----------

/** Chat budget: every character costs one token. */
export const CHAT_TOKENS_MAX = 400;
/** Tokens come back at this rate. */
export const CHAT_TOKENS_PER_SECOND = 3;
/** Longest single message (it can never cost more than a full budget). */
export const CHAT_MESSAGE_MAX = 400;
/** Messages a lobby keeps for people who join (in memory only). */
export const CHAT_HISTORY = 40;

/** Chat text: trimmed, no control characters, 1..CHAT_MESSAGE_MAX characters. */
export const ChatTextSchema = z
  .string()
  .transform((s) => s.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, "").trim())
  .pipe(z.string().min(1).max(CHAT_MESSAGE_MAX));

export const ChatMessageSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  text: z.string(),
  at: z.number().int(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * The chat token bucket (pure, so the client can show the same numbers).
 * Sending costs one token per character. Sending more than you have times you
 * out: you cannot send again until the bucket is full (400) again.
 */
export interface ChatBudget {
  tokens: number;
  /** When the tokens were last counted (ms). */
  at: number;
  /** Timed out until this time (ms), or 0. */
  mutedUntil: number;
}

export function freshBudget(now: number): ChatBudget {
  return { tokens: CHAT_TOKENS_MAX, at: now, mutedUntil: 0 };
}

/** Tokens right now (refilled since `at`). */
export function tokensAt(b: ChatBudget, now: number): number {
  return Math.min(CHAT_TOKENS_MAX, b.tokens + (Math.max(0, now - b.at) / 1000) * CHAT_TOKENS_PER_SECOND);
}

export type ChatSpend = { ok: true; budget: ChatBudget } | { ok: false; budget: ChatBudget; mutedUntil: number };

/** Try to send `length` characters at `now`. */
export function spendChat(b: ChatBudget, length: number, now: number): ChatSpend {
  const tokens = tokensAt(b, now);
  if (b.mutedUntil > now) return { ok: false, budget: { tokens, at: now, mutedUntil: b.mutedUntil }, mutedUntil: b.mutedUntil };
  if (length > tokens) {
    // Spamming: timed out until the bucket is full again.
    const mutedUntil = now + Math.ceil(((CHAT_TOKENS_MAX - tokens) / CHAT_TOKENS_PER_SECOND) * 1000);
    return { ok: false, budget: { tokens, at: now, mutedUntil }, mutedUntil };
  }
  return { ok: true, budget: { tokens: tokens - length, at: now, mutedUntil: 0 } };
}

// ---------- cigars ----------

/** A cigar on your wizard: none, held (unlit), or lit (glowing tip, smoke). */
export const CigarSchema = z.enum(["none", "unlit", "lit"]);
export type Cigar = z.infer<typeof CigarSchema>;

/** The cigar button cycles: take one → light it → put it out. */
export function nextCigar(c: Cigar): Cigar {
  return c === "none" ? "unlit" : c === "unlit" ? "lit" : "none";
}

// ---------- slot machine ----------

export const SLOT_SYMBOLS = ["cherry", "lemon", "bell", "bar", "seven", "hat"] as const;
export type SlotSymbol = (typeof SLOT_SYMBOLS)[number];
export const SLOT_FACES: Record<SlotSymbol, string> = { cherry: "🍒", lemon: "🍋", bell: "🔔", bar: "BAR", seven: "7", hat: "🎩" };
/** How often each symbol appears on a reel (out of the total). */
export const SLOT_WEIGHTS: Record<SlotSymbol, number> = { cherry: 7, lemon: 6, bell: 4, bar: 3, seven: 2, hat: 1 };
/** Three of a kind pays the bet times this. */
export const SLOT_TRIPLE: Record<SlotSymbol, number> = { cherry: 5, lemon: 8, bell: 20, bar: 40, seven: 80, hat: 200 };
/** Exactly two cherries pay the bet times this. About 93% of coins bet come back over time (see slotReturnRate). */
export const SLOT_TWO_CHERRIES = 2;
export const SLOT_BET_MIN = 1;
export const SLOT_BET_MAX = 100;
export const SlotBetSchema = z.number().int().min(SLOT_BET_MIN).max(SLOT_BET_MAX);

export type Reels = [SlotSymbol, SlotSymbol, SlotSymbol];

const WEIGHT_TOTAL = SLOT_SYMBOLS.reduce((s, x) => s + SLOT_WEIGHTS[x], 0);

function reel(rng: Rng): SlotSymbol {
  let r = rng.int(WEIGHT_TOTAL);
  for (const s of SLOT_SYMBOLS) {
    r -= SLOT_WEIGHTS[s];
    if (r < 0) return s;
  }
  return "cherry";
}

export function spinReels(rng: Rng = secureRng): Reels {
  return [reel(rng), reel(rng), reel(rng)];
}

/** Coins paid back for a spin (0 = lost the bet). Includes the bet itself. */
export function slotPayout(reels: Reels, bet: number): number {
  const [a, b, c] = reels;
  if (a === b && b === c) return bet * SLOT_TRIPLE[a];
  const cherries = reels.filter((r) => r === "cherry").length;
  return cherries === 2 ? bet * SLOT_TWO_CHERRIES : 0;
}

/** Expected payback per coin bet (the house keeps the rest), computed exactly. */
export function slotReturnRate(): number {
  let total = 0;
  for (const a of SLOT_SYMBOLS) {
    for (const b of SLOT_SYMBOLS) {
      for (const c of SLOT_SYMBOLS) {
        const p = (SLOT_WEIGHTS[a] * SLOT_WEIGHTS[b] * SLOT_WEIGHTS[c]) / WEIGHT_TOTAL ** 3;
        total += p * slotPayout([a, b, c], 1);
      }
    }
  }
  return total;
}

export const SlotSpinBodySchema = z.object({ bet: SlotBetSchema });
export type SlotSpinBody = z.infer<typeof SlotSpinBodySchema>;
export const SlotSpinResponseSchema = z.object({
  reels: z.tuple([z.enum(SLOT_SYMBOLS), z.enum(SLOT_SYMBOLS), z.enum(SLOT_SYMBOLS)]),
  bet: z.number().int(),
  payout: z.number().int(),
  coins: z.number().int(),
});
export type SlotSpinResponse = z.infer<typeof SlotSpinResponseSchema>;
